import { PaymentMethodEnum, PrimaryButton } from '@systemeio/ui'
import { storeDefaultErrors } from 'modules/stores/constants/store-default-values'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreEdit } from 'modules/stores/store/hooks/use-store-edit'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { StoreErrorsInterface } from 'modules/stores/types/stores-interface'
import React, { useEffect, useState } from 'react'
import EditModal from 'shared/components/edit-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { usePaymentMethodsByCurrency } from 'shared/hooks/use-payment-methods-by-currency'
import { twMerge } from 'tailwind-merge'

interface PaymentMethodsEditModalProps {
  errors: Pick<StoreErrorsInterface, 'paymentMethods'>
}

export const allowedDSPaymentMethods: PaymentMethodEnum[] = [
  PaymentMethodEnum.stripe_card,
  PaymentMethodEnum.stripe_ideal,
  PaymentMethodEnum.stripe_bancontact,
  PaymentMethodEnum.stripe_applepay,
  PaymentMethodEnum.paypal_soap,
  PaymentMethodEnum.paypal_rest,
  PaymentMethodEnum.gocardless,
  PaymentMethodEnum.paystack_gh_card,
  PaymentMethodEnum.paystack_ke_card,
  PaymentMethodEnum.paystack_ng_card,
  PaymentMethodEnum.paystack_za_card,
  PaymentMethodEnum.paystack_eg_card,
  PaymentMethodEnum.paystack_ci_card,
  PaymentMethodEnum.cash_on_delivery,
  PaymentMethodEnum.secure_and_pay,
  PaymentMethodEnum.mollie,
  PaymentMethodEnum.flutterwave,
  PaymentMethodEnum.mercadopago_ar_card,
  PaymentMethodEnum.mercadopago_br_card,
  PaymentMethodEnum.mercadopago_cl_card,
  PaymentMethodEnum.mercadopago_co_card,
  PaymentMethodEnum.mercadopago_mx_card,
  PaymentMethodEnum.mercadopago_br_boleto,
  PaymentMethodEnum.mercadopago_br_pix,
  PaymentMethodEnum.razorpay,
  PaymentMethodEnum.xendit_id_card,
  PaymentMethodEnum.xendit_ph_card,
]

export default function PaymentMethodsEditModal({
  errors: propsErrors,
}: PaymentMethodsEditModalProps) {
  const { storeId } = useStoreQuery()
  const { data: store } = useStore(storeId)
  const [errors, setErrors] = useState(storeDefaultErrors)
  const [opened, setOpened] = useState(false)
  const { t } = useLocoTranslation()

  const noPaymentMethods = !store?.paymentMethods || store.paymentMethods.length === 0

  useEffect(() => {
    if (noPaymentMethods) {
      setErrors(prev => ({ ...prev, ...propsErrors }))
    }
  }, [propsErrors, noPaymentMethods])

  const { onSave } = useStoreEdit({ setErrors })

  const onClose = () => setOpened(false)

  const caption = t('dashboard.store.product.checkout.payment_methods')

  const { currencyAndPaymentMethodsEditFields } = usePaymentMethodsByCurrency({
    currency: store?.currency,
    errors,
    allowedPaymentMethods: allowedDSPaymentMethods,
  })

  return (
    <>
      <PrimaryButton
        className={twMerge(
          'w-fit',
          noPaymentMethods && !!propsErrors.paymentMethods && 'bg-danger hover:bg-danger',
        )}
        disabled={!store}
        onClick={() => setOpened(true)}
      >
        {t('dashboard.store.product.checkout.edit_payment_methods')}
      </PrimaryButton>
      {store && (
        <EditModal
          caption={caption}
          opened={opened}
          onClose={onClose}
          instance={store}
          toastCaption={t('dashboard.actions.updated', { module: caption })}
          editable={[...currencyAndPaymentMethodsEditFields]}
          onEdit={data => onSave({ ...store, ...data })}
        />
      )}
    </>
  )
}
